import { useEffect } from "react";
import Navigation from "@/components/Navigation";
import FooterAlt from "@/components/FooterAlt";
import StructuredData from "@/components/StructuredData";
import { ExternalLink, Calendar } from "lucide-react";

interface NewsItem {
  title: string;
  source: string;
  date: string;
  url: string;
  excerpt: string;
}

const newsItems: NewsItem[] = [
  {
    title: "AMX Ventures-Backed Arun Mohan Joins Actualize to Lead Growth of Arabic Voice AI",
    source: "Entrepreneur Middle East",
    date: "2024",
    url: "https://www.entrepreneur.com/en-ae/starting-a-business/amx-ventures-backed-arun-mohan-joins-actualize-to/501270",
    excerpt: "AMX Ventures-backed Arun Mohan takes on a leadership role at Actualize to drive the expansion of Arabic voice AI technology."
  },
  {
    title: "Actualize Picks Arun Mohan as Equity Partner as It Scales Arabic Voice Intelligence Stack",
    source: "Campaign Middle East",
    date: "2024",
    url: "https://campaignme.com/actualize-picks-arun-mohan-as-equity-partner-as-it-scales-arabic-voice-intelligence-stack/",
    excerpt: "Actualize announces Arun Mohan as equity partner to accelerate growth of their Arabic voice intelligence platform."
  }
];

const News = () => {
  useEffect(() => {
    document.title = "News & Press | AMX Ventures";
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute("content", "Latest news and press coverage featuring AMX Ventures and our portfolio companies.");
    }
  }, []);

  const newsSchema = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    "name": "AMX Ventures News & Press",
    "description": "Latest news and press coverage featuring AMX Ventures and portfolio companies.",
    "publisher": {
      "@type": "Organization",
      "name": "AMX Ventures"
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <StructuredData data={newsSchema} />
      <Navigation />
      
      <main className="pt-24 pb-16">
        <div className="container mx-auto px-4 max-w-4xl">
          <header className="mb-12">
            <h1 className="text-4xl md:text-5xl font-display font-bold text-foreground mb-4">
              News & Press
            </h1>
            <p className="text-lg text-muted-foreground">
              Latest coverage featuring AMX Ventures and our portfolio companies.
            </p>
          </header>

          <div className="space-y-6">
            {newsItems.map((item, index) => (
              <a
                key={index}
                href={item.url}
                target="_blank"
                rel="noopener noreferrer"
                className="block group"
              >
                <article className="p-6 rounded-xl border border-border bg-card hover:border-primary/50 transition-all duration-300 hover:shadow-lg">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 text-sm text-muted-foreground mb-2">
                        <span className="font-medium text-primary">{item.source}</span>
                        <span>•</span>
                        <span className="flex items-center gap-1">
                          <Calendar className="w-3.5 h-3.5" />
                          {item.date}
                        </span>
                      </div>
                      <h2 className="text-xl font-semibold text-foreground group-hover:text-primary transition-colors mb-2">
                        {item.title}
                      </h2>
                      <p className="text-muted-foreground">
                        {item.excerpt}
                      </p>
                    </div>
                    <ExternalLink className="w-5 h-5 text-muted-foreground group-hover:text-primary transition-colors flex-shrink-0 mt-1" />
                  </div>
                </article>
              </a>
            ))}
          </div>
        </div>
      </main>

      <FooterAlt />
    </div>
  );
};

export default News;
